import type { ScopedThreadRef } from "@cleo/contracts";
import { BorderBeam } from "@neucleos/motion-primitives";
import { Archive, ChevronRight, EllipsisVertical, Plus } from "lucide-react";
import type { ComponentProps, CSSProperties, DragEvent, ReactElement, ReactNode } from "react";

import { cn } from "~/lib/utils";
import type { RailGroupRow, RailSessionRow } from "~/neucleos/railNavigationModel";
import {
  ROOT_SESSION_STATUS_LABELS,
  type RootSessionStatus,
  rootSessionStatusRingClass,
} from "~/rootSessionStrip";
import { SESSION_RAIL_ACCENT_READ } from "~/sessionAccent";
import { PipCluster } from "./PipCluster";
import {
  RAIL_ARCHIVE_CONFIRM_CLASS,
  RAIL_ROW_CONTAINER_CLASS,
  RAIL_WIDE_ROW_ONLY_CLASS,
  RAIL_WIDE_ROW_SEAT_CLASS,
  railAddedSeatsClass,
  railFloatingMenuClass,
  railFloatingSeatClass,
} from "./railArchiveConfirm";
import { RailHoverCard } from "./RailHoverCard";
import type { RailHoverFacts } from "./railHoverCard.logic";
import { RoomHeader } from "./RoomHeader";
import { RoomRailDraftMark } from "./RoomRailDraftMark";
import type { RoomRailCompactMetrics } from "./RoomRailSection.logic";
import { SessionPin } from "./SessionPin";
import { SessionRing } from "./SessionRing";
import { SessionRow } from "./SessionRow";

/**
 * RM-CAN-14: THE ONE ROW RENDERER of the Room Rail. It draws the navigation
 * model (`railNavigationModel.ts`) — groups over rows over nested rows — and
 * never asks which stance filled it. Everything a stance knows arrives through
 * `RailRowOrgans`: a World supplies governed organs (liveness, pins, the
 * retirement ceremony, the persisted order), Local supplies ungoverned ones
 * (threads, folders, the new-child ask). An organ a stance lacks is `null` and
 * its seat is simply not drawn; nothing here reads a governed type.
 *
 * Structure is the contract the RM14 oracle reads: a group is
 * `[data-room-rail-room-block]`, a row is `[data-room-rail-session]`, and a
 * row's descendants live inside its `[data-room-rail-family]`.
 */
const ROW_PX = 30;

type Position = { readonly x: number; readonly y: number };

export interface RailFileDropHandlers {
  readonly onDragOver: (event: DragEvent<HTMLElement>) => void;
  readonly onDragLeave: (event: DragEvent<HTMLElement>) => void;
  readonly onDrop: (event: DragEvent<HTMLElement>) => void;
}

/** A session Cleo launched for a hand: what she asked it to do. */
export interface RailHand {
  readonly ask: string;
}

export interface RailRowOrgans {
  readonly wide: boolean;
  /**
   * The principal, 16 Oct 2026 ("showing session that cleo launches for a
   * hand, as a nested item under the main workroom session"): a row Cleo
   * launched for a hand, or null. Absent where the stance has none (Local).
   */
  readonly hand?: ((row: RailSessionRow) => RailHand | null) | undefined;
  /** The rail is scoped to one group (a workroom's chip). */
  readonly scopedGroup: boolean;
  readonly addressedId: string | null;
  /** The addressed row stands in the Pinned list, so its room copy is not "current". */
  readonly activePin: boolean;
  /** A live search unfolds every ancestor of a match. */
  readonly searching: boolean;
  /** The scope the fold choices are kept under ("all", "needs", "working"). */
  readonly filterKey: string;
  readonly foldChoices: Readonly<Record<string, boolean>>;
  readonly onFoldRow: (foldKey: string, open: boolean) => void;
  readonly status: (id: string) => RootSessionStatus | null;
  readonly descendantStatus: (id: string) => RootSessionStatus | null;
  readonly groupLabel: (groupId: string) => string | null;
  readonly address: (row: RailSessionRow) => void;
  readonly openMenu: (row: RailSessionRow, position: Position, pinShortcut: boolean) => void;
  readonly threadRefOf: (row: RailSessionRow) => ScopedThreadRef | null;
  readonly hoverFacts: (
    row: RailSessionRow,
    meta: ReadonlyArray<string | null>,
  ) => RailHoverFacts | null;
  readonly hoverDisabled: (row: RailSessionRow) => boolean;
  /** Reorder wrappers; a stance without an arrangement returns the children keyed. */
  readonly wrapRow: (row: RailSessionRow, children: ReactNode) => ReactNode;
  readonly wrapPinnedRow: (row: RailSessionRow, children: ReactNode) => ReactNode;
  readonly belowRow: (row: RailSessionRow) => ReactNode;
  readonly pins: {
    readonly pinned: (id: string) => boolean;
    readonly toggle: (id: string) => void;
  } | null;
  readonly retire: ((row: RailSessionRow) => void) | null;
  /**
   * The ungoverned leaving verb (Local archives a conversation); a World retires
   * instead. When the stance asks before archiving, the row named by
   * `confirmingId` shows "Confirm" in the seat, and the pointer leaving that row
   * withdraws the question.
   */
  readonly archive: {
    readonly confirmingId: string | null;
    readonly onPress: (row: RailSessionRow) => void;
    readonly onLeave: () => void;
  } | null;
  /** Delete/Backspace on a focused chip. */
  readonly requestClose: ((row: RailSessionRow) => void) | null;
  readonly fileDrop: {
    readonly targetId: string | null;
    readonly handlers: (row: RailSessionRow) => RailFileDropHandlers;
  } | null;
  readonly compactMetrics: ((row: RailSessionRow) => RoomRailCompactMetrics) | null;
  readonly rename: {
    readonly id: string;
    readonly title: string;
    readonly onChange: (title: string) => void;
    readonly onCommit: () => void;
    readonly onCancel: () => void;
  } | null;
  /**
   * Where a row's hover-only seats stand. "floating" (both stances since 20 September
   * 2026): the unpinned pin, the menu and the retire glyph join the seats a stance adds in
   * one cluster over the name's tail, shown on hover or focus — at the dock's default
   * 160px the reserved seats left a Local row's name 0px, and a click on the row's centre
   * landed on the pin seat and PINNED the conversation instead of opening it (witnessed in
   * a fresh profile, 20 September 2026); a World's reserved seats left its names 12px at
   * the same width (measured in the Dev Desk the same day). "reserved": each seat keeps
   * its width at rest; the Pinned list's shortcuts keep it (RM13: revealing menu and
   * retire never moves a pin's hit target). A pinned row's pin is information and stays
   * in line either way.
   */
  readonly hoverSeats: "reserved" | "floating";
  /** The new-child verb: a named ask under the parent row, then one birth. */
  readonly childAsk: {
    readonly parentId: string | null;
    readonly title: string;
    readonly busy: boolean;
    readonly onOpen: (row: RailSessionRow) => void;
    readonly onChange: (title: string) => void;
    readonly onSubmit: (row: RailSessionRow) => void;
    readonly onCancel: () => void;
  } | null;
}

export interface RailGroupOrgans {
  readonly wide: boolean;
  readonly open: (group: RailGroupRow) => boolean;
  readonly header: (group: RailGroupRow, open: boolean) => ComponentProps<typeof RoomHeader>;
  readonly renderHeader?: ((props: ComponentProps<typeof RoomHeader>) => ReactElement) | undefined;
  readonly wrapGroup: (group: RailGroupRow, children: ReactNode) => ReactNode;
  /** Below an open group's rows (a scoped room's Retired group). */
  readonly foot: (group: RailGroupRow) => ReactNode;
  /** What an open group with no rows says. */
  readonly emptyBody: ((group: RailGroupRow) => ReactNode) | null;
}

function countRows(rows: ReadonlyArray<RailSessionRow>): number {
  return rows.reduce((total, row) => total + 1 + countRows(row.children), 0);
}

function compactLine(id: string, metrics: RoomRailCompactMetrics): ReactElement {
  const parts = [
    metrics.status === null ? null : (
      <span data-room-rail-compact-status={id} key="status">
        {metrics.status}
      </span>
    ),
    metrics.changeRequest === null ? null : (
      <span
        className="rounded-sm border border-border px-1 uppercase tracking-wide"
        data-room-rail-compact-pr={metrics.changeRequest}
        key="pr"
      >
        PR {metrics.changeRequest}
      </span>
    ),
    metrics.time === null ? null : (
      <span data-room-rail-compact-time={id} key="time">
        {metrics.time}
      </span>
    ),
  ].filter((part) => part !== null);
  return (
    <span
      className="flex items-center gap-1 truncate text-[10px] text-muted-foreground"
      data-room-rail-compact-line={id}
    >
      {parts.length === 0 ? <span>Quiet</span> : parts}
    </span>
  );
}

/** The named ask under a parent row: Enter births the child, Escape drops it. */
function childAskRow(organs: RailRowOrgans, row: RailSessionRow, depth: number): ReactNode {
  const ask = organs.childAsk;
  if (ask === null || ask.parentId !== row.id) return null;
  return (
    <div
      className="flex h-6 w-full min-w-0 items-center gap-1.5 pr-1.5"
      data-room-rail-new-child-ask={row.id}
      style={{ paddingLeft: `${6 + depth * 18}px` }}
    >
      <Plus aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />
      <input
        aria-label={`Name the new child of ${row.label}`}
        autoFocus
        className="min-w-0 flex-1 bg-transparent text-[11px] text-foreground outline-none placeholder:text-muted-foreground"
        data-room-rail-new-child-input={row.id}
        disabled={ask.busy}
        onBlur={() => {
          if (!ask.busy && ask.title.trim() === "") ask.onCancel();
        }}
        onChange={(event) => ask.onChange(event.target.value)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") ask.onSubmit(row);
          else if (event.key === "Escape") ask.onCancel();
        }}
        placeholder="What should the child do?"
        value={ask.title}
      />
    </div>
  );
}

/** A hand Cleo launched under this row is working: the row opens so it is seen. */
function workingHand(organs: RailRowOrgans, row: RailSessionRow): boolean {
  return row.children.some(
    (child) =>
      (organs.hand?.(child) != null && organs.status(child.id) === "working") ||
      workingHand(organs, child),
  );
}

/** A nested row and its own children, depth-first (RM-CAN-10 law 3). */
export function renderRailSubRows(
  organs: RailRowOrgans,
  rows: ReadonlyArray<RailSessionRow>,
  depth: number,
  parentLabel: string,
): ReactNode[] {
  return rows.map((row) => {
    const own = organs.status(row.id);
    const pinned = organs.pins?.pinned(row.id) ?? false;
    const hand = organs.hand?.(row) ?? null;
    return organs.wrapRow(
      row,
      <div data-room-rail-family={row.id} key={row.id}>
        <SessionRow
          current={row.id === organs.addressedId && !organs.activePin}
          depth={depth}
          key={row.id}
          label={row.label}
          onAddress={() => organs.address(row)}
          pinned={pinned}
          onTogglePin={organs.pins === null ? undefined : () => organs.pins?.toggle(row.id)}
          onMenu={(position) => organs.openMenu(row, position, false)}
          onRetire={organs.retire === null ? undefined : () => organs.retire?.(row)}
          onArchive={organs.archive === null ? undefined : () => organs.archive?.onPress(row)}
          archiveConfirming={organs.archive?.confirmingId === row.id}
          onArchiveLeave={organs.archive === null ? undefined : organs.archive.onLeave}
          onNewChild={organs.childAsk === null ? undefined : () => organs.childAsk?.onOpen(row)}
          renaming={organs.rename?.id === row.id ? organs.rename : null}
          floatSeats={organs.hoverSeats === "floating"}
          sessionId={row.id}
          status={own}
          hand={hand === null ? null : { ask: hand.ask, parentLabel }}
          preview={organs.hoverFacts(row, [
            pinned ? "Pinned" : null,
            hand === null ? `Sub-session · depth ${depth}` : `Cleo's hand · for ${parentLabel}`,
            hand === null ? null : `Asked: ${hand.ask}`,
            own === null ? "Quiet" : ROOT_SESSION_STATUS_LABELS[own],
          ])}
          previewDisabled={organs.hoverDisabled(row)}
          draftMark={
            <RoomRailDraftMark
              current={row.id === organs.addressedId}
              group="row"
              label={row.label}
              sessionId={row.id}
              threadRef={organs.threadRefOf(row)}
            />
          }
          fileDrop={organs.fileDrop?.handlers(row)}
          fileDropTarget={organs.fileDrop?.targetId === row.id}
        />
        {organs.belowRow(row)}
        {childAskRow(organs, row, depth + 1)}
        {renderRailSubRows(organs, row.children, depth + 1, row.label)}
      </div>,
    );
  });
}

/** A root row's chip: the FD-CAN-13 door on the rail's one seat. */
export function renderRailChip(
  organs: RailRowOrgans,
  row: RailSessionRow,
  breadcrumb?: string,
  pinShortcut = false,
): ReactNode {
  const { wide } = organs;
  const selected = row.id === organs.addressedId && (!organs.activePin || pinShortcut);
  const pinned = organs.pins?.pinned(row.id) ?? false;
  const own = organs.status(row.id);
  // FD-CAN-15 carried: the descendants' rollup (any depth, folded or not)
  // is announced on the chip, so live work below the fold surfaces once.
  const ghost = wide || pinShortcut || organs.scopedGroup ? organs.descendantStatus(row.id) : null;
  const asking = organs.childAsk?.parentId === row.id;
  const plural = row.children.length > 0;
  const foldKey = `${organs.filterKey}:${row.id}`;
  // UP13: a live search unfolds every ancestor of a match; the saved fold
  // choice returns untouched once the query clears. A row being asked for a
  // child stands open so the ask is seen under it.
  const unfolded =
    (plural &&
      (organs.searching ||
        (organs.foldChoices[foldKey] ??
          (organs.filterKey !== "all" || row.addressed || workingHand(organs, row))))) ||
    asking;
  const childStatuses = row.children.map((child) => organs.status(child.id));
  const statusWord =
    own === null ? (wide ? "" : " · Quiet") : ` · ${ROOT_SESSION_STATUS_LABELS[own]}`;
  const rowTitle = `${row.label}${statusWord}`;
  const location = breadcrumb ? `${pinShortcut || !wide ? "Room: " : ""}${breadcrumb}` : undefined;
  // RM-CAN-10 hover card (2026-09-15): the full name, the room, the
  // standing, the times and the last user message — beside the rail, where
  // a truncated row cannot say them.
  const groupLabel = breadcrumb ?? organs.groupLabel(row.groupId);
  const hoverFacts = organs.hoverFacts(row, [
    pinned ? "Pinned" : null,
    groupLabel === null ? null : `Room: ${groupLabel}`,
    own === null ? "Quiet" : ROOT_SESSION_STATUS_LABELS[own],
    ghost === null ? null : `Sub-sessions: ${ROOT_SESSION_STATUS_LABELS[ghost]}`,
  ]);
  const hoverDisabled = organs.hoverDisabled(row);
  const hover = (element: ReactElement): ReactElement =>
    hoverFacts === null ? (
      element
    ) : (
      <RailHoverCard disabled={hoverDisabled} facts={hoverFacts} trigger={element} />
    );
  // UP13: a compact row stands 36px with status, PR badge and time under
  // the label; a breadcrumb row already spends its second line on the room.
  const compactRow =
    wide && organs.compactMetrics !== null && !breadcrumb ? organs.compactMetrics(row) : null;
  const announcement = `${pinned ? "Pinned · " : ""}${rowTitle}${location ? ` · ${location}` : ""}${ghost === null ? "" : `, descendants: ${ROOT_SESSION_STATUS_LABELS[ghost]}`}`;
  const fileDropTarget = organs.fileDrop?.targetId === row.id;
  const renaming = organs.rename?.id === row.id ? organs.rename : null;
  const glyph =
    "pointer-events-none inline-flex size-3.5 shrink-0 items-center justify-center rounded-sm opacity-0 hover:bg-accent hover:opacity-100 group-hover/session:pointer-events-auto group-hover/session:opacity-60 group-focus-within/session:pointer-events-auto group-focus-within/session:opacity-60";
  const floating = organs.hoverSeats === "floating" && !pinShortcut;
  const pinSeat =
    organs.pins === null ? null : (
      <SessionPin label={row.label} pinned={pinned} onToggle={() => organs.pins?.toggle(row.id)} />
    );
  // The retire glyph, in line (reserved) or in the cluster (floating): the same door.
  const retireSeat = (className: string): ReactElement => (
    <span
      aria-hidden="true"
      className={className}
      data-room-rail-retire={row.id}
      onClick={(event) => {
        event.stopPropagation();
        organs.retire?.(row);
      }}
      title="Retire session"
    >
      <Archive className="size-3" />
    </span>
  );
  const menuSeat = (
    <span
      aria-hidden="true"
      className={floating ? railFloatingMenuClass("session") : glyph}
      data-room-rail-menu={row.id}
      onClick={(event) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        organs.openMenu(row, { x: rect.left, y: rect.bottom + 4 }, pinShortcut);
      }}
    >
      <EllipsisVertical className="size-3" />
    </span>
  );
  const chip = (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      aria-label={announcement}
      className={cn(
        "ncl-accent-door group/session relative focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
        wide
          ? "flex w-full min-w-0 items-center gap-1.5 rounded-[10px] px-2 text-left text-xs"
          : // FD-CAN-29: a chip that opens a panel is a tab and is square —
            // r=10 at 28px (FD-CAN-17's shape law), concentric with the
            // left card's carve in both densities.
            "flex size-7 items-center justify-center rounded-[10px]",
        selected && "font-medium",
        floating && wide && RAIL_ROW_CONTAINER_CLASS,
        fileDropTarget && "ring-1 ring-ring bg-accent/40",
      )}
      data-room-rail-compact={compactRow ? "true" : undefined}
      data-room-rail-depth={0}
      data-room-rail-pin={pinShortcut ? row.id : undefined}
      data-room-rail-descendant-status={ghost ?? undefined}
      data-room-rail-door={row.id}
      data-room-rail-file-drop={fileDropTarget ? "true" : undefined}
      data-room-rail-session={row.id}
      data-room-rail-status={own ?? undefined}
      key={row.id}
      {...organs.fileDrop?.handlers(row)}
      onMouseLeave={
        organs.archive?.confirmingId === row.id ? () => organs.archive?.onLeave() : undefined
      }
      onClick={() => organs.address(row)}
      // FD-CAN-20: right-click is the fast door on EVERY row, in both densities.
      onContextMenu={(event) => {
        event.preventDefault();
        organs.openMenu(row, { x: event.clientX, y: event.clientY }, pinShortcut);
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          organs.openMenu(row, { x: rect.left, y: rect.bottom + 4 }, pinShortcut);
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          organs.address(row);
          return;
        }
        if (event.key !== "Delete" && event.key !== "Backspace") return;
        if (organs.requestClose === null) return;
        event.preventDefault();
        organs.requestClose(row);
      }}
      style={
        {
          height: `${wide && breadcrumb ? 44 : compactRow ? compactRow.heightPx : ROW_PX - 2}px`,
          "--door-accent": SESSION_RAIL_ACCENT_READ,
        } as CSSProperties
      }
    >
      {/* FD-CAN-21: motion is information — the beam runs ONLY while this
          row's own liveness stands at "working". */}
      {own === "working" ? <BorderBeam color="var(--door-accent)" /> : null}
      <SessionRing className={wide ? "size-3.5" : "size-4"} status={own} />
      {/* WL1 carried: the descendants' rollup (any depth, folded or not)
          stands on the chip as the hollow ghost — work below the fold
          surfaces once on its visible ancestor. */}
      {ghost !== null ? (
        <span
          aria-hidden="true"
          className={`size-1.5 shrink-0 rounded-full border forced-colors:border-[ButtonText] ${rootSessionStatusRingClass(ghost)}`}
          data-room-rail-ghost={ghost}
        />
      ) : null}
      {wide ? (
        renaming !== null ? (
          // FD-CAN-20: Rename edits inline on the chip and commits through
          // the same updateMetadata door the header rename uses.
          <input
            autoFocus
            className="min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none"
            data-room-rail-rename=""
            onBlur={() => renaming.onCommit()}
            onChange={(event) => renaming.onChange(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter") renaming.onCommit();
              else if (event.key === "Escape") renaming.onCancel();
            }}
            value={renaming.title}
          />
        ) : (
          <span className="min-w-0 flex-1 truncate text-foreground">
            {row.label}
            {breadcrumb ? (
              <span className="block truncate text-[10px] text-muted-foreground">{location}</span>
            ) : compactRow ? (
              compactLine(row.id, compactRow)
            ) : null}
          </span>
        )
      ) : null}
      {wide && plural ? <PipCluster noun="sub-sessions" statuses={childStatuses} /> : null}
      {wide ? (
        <>
          <RoomRailDraftMark
            current={row.id === organs.addressedId}
            label={row.label}
            sessionId={row.id}
            threadRef={organs.threadRefOf(row)}
          />
          {/* Hover and keyboard focus reveal the row's verbs together. */}
          {organs.pins !== null && (!floating || pinned) ? pinSeat : null}
          {floating ? null : menuSeat}
          {organs.retire !== null && !floating ? retireSeat(glyph) : null}
        </>
      ) : null}
      {wide && !pinShortcut && (floating || organs.childAsk !== null || organs.archive !== null) ? (
        // The seats this stance adds float over the label's tail (railArchiveConfirm.ts):
        // they cost the name no width at rest and nothing under the pointer moves.
        <span
          className={railAddedSeatsClass("session", organs.archive?.confirmingId === row.id)}
          data-room-rail-added-seats={row.id}
          onClick={(event) => event.stopPropagation()}
          // Clear of what stays in line to its right: the fold, and when the seats are
          // reserved, the menu and the pin too.
          style={{ right: `${(plural ? 28 : 8) + (floating ? 0 : 40)}px` }}
        >
          {floating && !pinned ? <span className={RAIL_WIDE_ROW_ONLY_CLASS}>{pinSeat}</span> : null}
          {organs.childAsk !== null ? (
            <span
              aria-label={`New child of ${row.label}`}
              className={RAIL_WIDE_ROW_SEAT_CLASS}
              data-room-rail-new-child={row.id}
              onClick={(event) => {
                event.stopPropagation();
                organs.childAsk?.onOpen(row);
              }}
              role="button"
              title="New child"
            >
              <Plus aria-hidden="true" className="size-3" />
            </span>
          ) : null}
          {floating ? menuSeat : null}
          {floating && organs.retire !== null ? retireSeat(railFloatingSeatClass("session")) : null}
          {organs.archive === null ? null : organs.archive.confirmingId === row.id ? (
            // The second click, in view without a hover: it must not vanish mid-decision.
            <span
              aria-label={`Confirm archive ${row.label}`}
              className={RAIL_ARCHIVE_CONFIRM_CLASS}
              data-room-rail-archive-confirm={row.id}
              onClick={(event) => {
                event.stopPropagation();
                organs.archive?.onPress(row);
              }}
              role="button"
            >
              Confirm
            </span>
          ) : (
            <span
              aria-label={`Archive ${row.label}`}
              className={RAIL_WIDE_ROW_SEAT_CLASS}
              data-room-rail-archive-session={row.id}
              onClick={(event) => {
                event.stopPropagation();
                organs.archive?.onPress(row);
              }}
              role="button"
              title="Archive"
            >
              <Archive className="size-3" />
            </span>
          )}
        </span>
      ) : null}
      {wide && plural ? (
        // RM-CAN-10 law 2: the chevron is the disclosure; click folds, never addresses.
        <span
          aria-expanded={unfolded}
          aria-hidden="true"
          className="flex size-3.5 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground"
          data-room-rail-fold={row.id}
          onClick={(event) => {
            event.stopPropagation();
            organs.onFoldRow(foldKey, !unfolded);
          }}
        >
          <ChevronRight
            className={cn(
              "size-3 transition-transform duration-[var(--duration-quick,150ms)] ease-[var(--ease-smooth-out,ease-out)] motion-reduce:transition-none",
              unfolded && "rotate-90",
            )}
          />
        </span>
      ) : null}
    </div>
  );
  if (pinShortcut)
    return organs.wrapPinnedRow(
      row,
      <>
        {hover(chip)}
        {wide ? organs.belowRow(row) : null}
      </>,
    );
  if (!wide)
    return hoverFacts === null ? (
      chip
    ) : (
      <RailHoverCard disabled={hoverDisabled} facts={hoverFacts} key={row.id} trigger={chip} />
    );
  if (!unfolded)
    return organs.wrapRow(
      row,
      <div data-room-rail-family={row.id} key={row.id}>
        {hover(chip)}
        {organs.belowRow(row)}
      </div>,
    );
  // The family block: the chip and its nested rows stand together, the
  // left card carving the addressed door out of it (FD-CAN-29).
  return organs.wrapRow(
    row,
    <div
      className="flex w-full min-w-0 flex-col gap-0.5"
      data-room-rail-door={row.id}
      data-room-rail-door-current={row.addressed && !organs.activePin ? "true" : undefined}
      data-room-rail-family={row.id}
      key={row.id}
    >
      {hover(chip)}
      {organs.belowRow(row)}
      {childAskRow(organs, row, 1)}
      {renderRailSubRows(organs, row.children, 1, row.label)}
    </div>,
  );
}

/** One group — a World's room or a Local folder — over its rows. */
export function renderRailGroup(
  groupOrgans: RailGroupOrgans,
  rowOrgans: RailRowOrgans,
  group: RailGroupRow,
): ReactNode {
  const { wide } = groupOrgans;
  const open = groupOrgans.open(group);
  const headerProps = groupOrgans.header(group, open);
  return groupOrgans.wrapGroup(
    group,
    <div
      // The header sits 8 above its first chip; chips keep the carve
      // rhythm's 16 between themselves (FD-CAN-29).
      className={cn("flex w-full min-w-0 flex-col gap-2", wide ? "" : "items-center")}
      data-open={open ? "true" : "false"}
      data-room-rail-room-block={group.id}
      data-room-rail-thread-count={countRows(group.sessions)}
      data-room-rail-parked-count={group.parked}
      key={group.id}
    >
      {groupOrgans.renderHeader ? (
        groupOrgans.renderHeader(headerProps)
      ) : (
        <RoomHeader {...headerProps} />
      )}
      {open ? (
        <div className={cn("flex w-full min-w-0 flex-col gap-2", wide ? "" : "items-center")}>
          <div className="flex min-w-0 flex-col" data-room-rail-sessions="">
            {group.sessions.map((row) => renderRailChip(rowOrgans, row))}
            {group.sessions.length === 0 && groupOrgans.emptyBody !== null
              ? groupOrgans.emptyBody(group)
              : null}
          </div>
          {groupOrgans.foot(group)}
        </div>
      ) : null}
    </div>,
  );
}
